import { Building2, Mail, Phone, Star } from 'lucide-react';

import { primaryFirst } from '../../../lib/crm/contactSheet';
import type { CRMContact } from '../../../types/crm';
import { REF } from '../../bureau/encre';
import { CallHistory } from '../../telephony/CallHistory';
import { EMAIL_LABELS, PHONE_LABELS } from '../ContactMultiFieldEditor';
import { Pastille, SheetBlock, SheetEmpty } from './EncreSheet';

const labelOf = (labels: { value: string; label: string }[], value: string | null | undefined): string | null =>
  labels.find((l) => l.value === value)?.label ?? value ?? null;

/** One way to reach the contact: its pastille, the link, its label and « principal ». */
function Reach({ icon, href, value, label, primary }: { icon: typeof Mail; href: string; value: string; label: string | null; primary: boolean }) {
  return (
    <li className="flex min-w-0 items-center gap-3 py-1">
      <Pastille icon={icon} />
      <a href={href} className={`min-w-0 truncate text-sm ${REF}`}>
        {value}
      </a>
      <span className="shrink-0 text-xs text-desk-muted">{[label, primary ? 'principal' : null].filter(Boolean).join(' · ')}</span>
    </li>
  );
}

/** « Coordonnées »: every mail and phone, the primary first, each a link that writes or calls. */
export function CoordinatesBlock({ contact }: { contact: CRMContact }) {
  const emails = primaryFirst(contact.emails);
  const phones = primaryFirst(contact.phones);
  // A contact without rows in the multi-value tables still has its own fields.
  const ownEmail = emails.length === 0 && contact.email ? [{ id: 'own-email', email: contact.email, label: null, is_primary: true }] : [];
  const ownPhone = phones.length === 0 && contact.phone ? [{ id: 'own-phone', phone: contact.phone, label: null, is_primary: true }] : [];
  const mails = [...emails, ...ownEmail];
  const numbers = [...phones, ...ownPhone];
  return (
    <SheetBlock title="Coordonnées">
      {mails.length + numbers.length === 0 ? (
        <SheetEmpty>Ni mail ni téléphone : « Modifier » permet d'en ajouter.</SheetEmpty>
      ) : (
        <ul className="m-0 list-none p-0">
          {mails.map((e) => (
            <Reach key={e.id} icon={Mail} href={`mailto:${e.email}`} value={e.email} label={labelOf(EMAIL_LABELS, e.label)} primary={Boolean(e.is_primary)} />
          ))}
          {numbers.map((p) => (
            <Reach key={p.id} icon={Phone} href={`tel:${p.phone}`} value={p.phone} label={labelOf(PHONE_LABELS, p.label)} primary={Boolean(p.is_primary)} />
          ))}
        </ul>
      )}
    </SheetBlock>
  );
}

/** « Entreprises »: the accounts the contact works with, the primary first; each opens its sheet. */
export function AccountsBlock({ contact, onOpenAccount }: { contact: CRMContact; onOpenAccount: (id: string) => void }) {
  const links = primaryFirst(contact.contact_accounts);
  return (
    <SheetBlock title="Entreprises" count={links.length}>
      {links.length === 0 ? (
        <SheetEmpty>Rattaché à aucun compte.</SheetEmpty>
      ) : (
        <ul className="m-0 list-none p-0">
          {links.map((link) => (
            <li key={link.id} className="flex min-w-0 items-center gap-3 py-1">
              <Pastille icon={Building2} />
              <button type="button" onClick={() => onOpenAccount(link.account_id)} className={`min-w-0 truncate text-sm ${REF}`}>
                {link.account?.name ?? 'Compte'}
              </button>
              <span className="flex shrink-0 items-center gap-1 text-xs text-desk-muted">
                {link.role}
                {link.is_primary && (
                  <>
                    <Star size={12} className="fill-current text-desk-signal" aria-hidden="true" /> principal
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SheetBlock>
  );
}

/** « Appels »: the calls with the contact, their recordings and transcripts (the telephony's own list). */
export function CallsBlock({ contactId }: { contactId: string }) {
  return (
    <SheetBlock title="Appels">
      <CallHistory contactId={contactId} />
    </SheetBlock>
  );
}
